<template>
  <div class="">
    <div class="border-b py-2 sm:flex sm:items-center">
      <div class="flex flex-auto items-center space-x-2">
        <div class="shrink-0">
          <!--          <mdicon name="email" size="26" class="text-orange-600/70" aria-hidden="true"></mdicon>-->
        </div>
        <div class="">
          <h1 class="text-xl font-semibold text-gray-700">{{ title }}</h1>
        </div>
      </div>
      <Button icon="pi pi-plus" :label="`Add ${types[props.type]}`" size="small" @click="onNewEmail" />
    </div>

    <div class="">
      <DataTable :value="allEmails" paginator :rows="25" :rows-per-page-options="[25, 50, 100]" removable-sort table-style="min-width: 50rem" class="" row-hover>
        <Column field="id" :header="`${props.type === 'chat' ? 'Channel' : types[props.type]}`" sortable>
          <template #body="slotProps">
            <div class="flex items-center">
              <mdicon :name="icons[props.type]" size="22" class="mr-1 text-slate-300" aria-hidden="true"></mdicon>
              <div class="cursor-pointer font-semibold hover:underline" @click="onEditEmail(slotProps.data)">{{ slotProps.data.id }}</div>
            </div>
          </template>
        </Column>
        <Column field="integrationName" header="Integration" sortable>
          <template #body="slotProps">
            <div class="flex items-center">
              <span class="cursor-pointer font-semibold hover:underline">{{ integrationsObj[slotProps.data.integrationName] }}</span>
            </div>
          </template>
        </Column>
        <Column field="botId" header="Bot" sortable>
          <template #body="slotProps">
            <router-link v-if="slotProps.data?.botId && slotProps.data.botId.length > 0" :to="{ path: `/bots/editor/${slotProps.data.botId}` }" _target="blank">
              <div class="flex items-center">
                <div class="cursor-pointer hover:underline">{{ getBotName(slotProps.data?.botId) }}</div>
                <mdicon name="open-in-new" size="18" class="ml-1 text-slate-300"></mdicon>
              </div>
            </router-link>
          </template>
        </Column>
        <Column field="updated" header="Last Updated" sortable>
          <template #body="slotProps"> {{ slotProps.data?.updated ? dayjs(slotProps.data.updated).format('MM/DD/YYYY') : '' }} </template>
        </Column>
        <Column field="updatedBy" header="Updated By" sortable> </Column>
        <Column style="width: 1%">
          <template #body="slotProps">
            <Button type="button" size="small" icon="pi pi-ellipsis-v" rounded severity="secondary" @click="(event) => toggleEmailMenu(event, slotProps?.data)" />
          </template>
        </Column>
      </DataTable>
    </div>
    <Menu id="overlay_menu" ref="emailMenu" :model="emailMenuItems" :popup="true">
      <template #item="{ item, props }">
        <a v-if="!item.hidden" class="flex items-center" v-bind="props.action">
          <mdicon :name="item.icon" size="18" class="mr-0.5 text-slate-400"></mdicon>
          <span class="ml-2">{{ item.label }}</span>
        </a>
      </template>
    </Menu>
    <ConfirmDialog group="delCat">
      <template #message="slotProps">
        <div class="flex flex-col gap-2">
          <div class="text-sm">Please confirm deleting {{ props.type }}</div>
          <div class="text-lg font-medium">{{ currentEmail.id }}</div>
        </div>
      </template>
    </ConfirmDialog>
    <SettingsDialogEditEmail ref="dialogEditEmail" :type="props.type" @save="onSaveEmail" />
  </div>
</template>

<script setup>
  import { useAuthStore } from '@/store/AuthStore';
  import { useSettingsStore } from '@/store/SettingsStore';
  import { useBotsStore } from '@/store/BotsStore';
  import { ref, computed, toRaw, onMounted, watch, watchEffect, reactive } from 'vue';
  import { useToast } from 'vue-toastification';
  import DataTable from 'primevue/datatable';
  import Column from 'primevue/column';
  import SettingsDialogEditEmail from '@/components/Settings/dialogs/SettingsDialogEditEmail.vue';
  import { fbDb } from '@/firebaseApp';
  import { collection, getDocs, onSnapshot } from 'firebase/firestore';
  import dayjs from 'dayjs';
  import Button from 'primevue/button';
  import Menu from 'primevue/menu';
  import ConfirmDialog from 'primevue/confirmdialog';
  import { useConfirm } from 'primevue/useconfirm';
  import { useCollection } from 'vuefire';
  const authStore = useAuthStore();
  const settingsStore = useSettingsStore();
  const botsStore = useBotsStore();
  const confirm = useConfirm();

  const props = defineProps({
    integrationsAllowed: {
      type: Array,
      default: () => [],
    },
    type: {
      type: String,
      default: '',
    },
  });

  const icons = {
    sms: 'phone-message',
    email: 'email',
    chat: 'forum-outline',
  };

  const types = {
    sms: 'SMS',
    email: 'Email',
    chat: 'Chat',
  };

  const dialogEditEmail = ref(null);
  const emailMenu = ref(null);
  const allEmails = ref([]);
  let integrations = ref([]);
  let integrationsObj = ref({});

  (async () => {
    await settingsStore.getElasticIndexSpan();
    const integrationsAllowed = props.integrationsAllowed.filter((ia) => settingsStore[`${ia}IntegrationEnabled`]);
    const integrationsSnapshot = await getDocs(collection(fbDb, `tenants/${authStore.tenantId}/integrations`));
    integrations.value = integrationsSnapshot.docs
      ?.filter((di) => integrationsAllowed.includes(di.id))
      .map((doc) => ({
        id: doc.id,
        name: doc.id.replace(/\s/g, '').replace(/^./, (match) => match.toUpperCase()),
      }));
    integrationsObj.value = integrations.value?.reduce((acc, el) => {
      return Object.assign(acc, { [el.id]: el.name });
    }, {});
    const integrationEmails = {};
    integrations.value.forEach((integration) => {
      const emailBotsRef = collection(fbDb, `tenants/${authStore.tenantId}/integrations/${integration.id}/${props.type}Bots`);
      onSnapshot(emailBotsRef, (snapshot) => {
        integrationEmails[integration.id] = snapshot.docs.map((doc) => ({
          id: doc.id,
          integrationName: integration.id,
          ...doc.data(),
        }));

        allEmails.value = Object.values(integrationEmails).flat();
      });
    });
  })();

  const currentEmail = ref(null);
  const emailMenuItems = ref([
    {
      label: 'Delete',
      icon: 'minus',
      command: () => {
        confirm.require({
          group: 'delCat',
          header: `Delete Email`,
          acceptIcon: 'pi pi-check',
          rejectIcon: 'pi pi-times',
          rejectClass: 'p-button-outlined p-button-sm',
          acceptClass: 'p-button-sm p-button-danger',
          rejectLabel: 'Cancel',
          acceptLabel: 'Delete',
          accept: () => {
            onDeleteEmail(currentEmail.value);
          },
          reject: () => {
            console.log(`Email delete canceled`);
          },
        });
      },
    },
  ]);

  async function onEditEmail(emailDoc) {
    console.log(`onEditEmail:`, emailDoc);
    dialogEditEmail.value.start(emailDoc, toRaw(integrations.value));
  }

  async function onNewEmail() {
    dialogEditEmail.value.start({}, toRaw(integrations.value));
  }

  async function onSaveEmail({ botId, id, senderEmail, integrationName, templateId, queueId }) {
    let payload = { botId, id, senderEmail };
    switch (integrationName) {
      case 'kustomer':
        payload.templateId = templateId;
        payload.queueId = queueId;
        break;
      case 'salesforce':
        payload.templateId = templateId;
        break;
    }

    if (integrationName === 'freshchat' && props.type === 'chat') {
      payload = { ...payload, source: 'FRESHCHAT' };
    }

    await settingsStore.updateChannel(integrationName, payload, props.type);
  }
  async function onDeleteEmail({ botId, id, updated, updatedBy, integrationName }) {
    await settingsStore.deleteChannel(integrationName, { botId, id, updated, updatedBy }, props.type);
  }

  function toggleEmailMenu(event, emailDoc) {
    currentEmail.value = emailDoc;
    emailMenu.value.toggle(event);
  }

  function getBotName(botId) {
    if (!botId) {
      return '-';
    }
    const bot = botsStore.getBot(botId);
    if (!bot) {
      return '-';
    }
    return bot?.name || '-';
  }
</script>
